(() => {
  const ENHANCED_ATTR = 'data-jp-subscription-enhanced';
  const ORIGINAL_CLASS = 'jp-subscription-original-hidden';

  const moneyPattern = /R\s?[\d\s,.]+/g;
  let activeSubscriptionOption = null;
  let sellingPlanGroups = null;
  let currentVariant = null;
  const syncedForms = new WeakSet();

  function cleanText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function getSubscriptionInfoModal() {
    let modal = document.querySelector('[data-jp-subscription-modal]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'jp-subscription-modal';
    modal.setAttribute('data-jp-subscription-modal', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="jp-subscription-modal__backdrop" data-jp-subscription-modal-close></div>
      <div class="jp-subscription-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="jp-subscription-modal-title">
        <button type="button" class="jp-subscription-modal__close" aria-label="Close" data-jp-subscription-modal-close>&times;</button>
        <h2 id="jp-subscription-modal-title">How Just Protein Subscriptions Work</h2>
        <ol>
          <li>
            <strong>Save 15% on every order</strong>
            <span>Choose your favourite products and get 15% off every subscription order.</span>
          </li>
          <li>
            <strong>Choose your subscription schedule</strong>
            <span>Select delivery every month or every 2 months, depending on what suits your routine best.</span>
          </li>
          <li>
            <strong>Your order is taken care of</strong>
            <span>We&rsquo;ll automatically process and ship your subscription order on your order date, so you never run out of your essentials.</span>
          </li>
          <li>
            <strong>Pause or cancel anytime</strong>
            <span>No contracts, no stress. Pause or cancel your subscription at any time by logging into your profile using the email address from your purchase.</span>
          </li>
          <li>
            <strong>Mix and match</strong>
            <span>Add subscription products and one-time purchases to the same cart and check out all at once. We&rsquo;ll take care of the rest.</span>
          </li>
        </ol>
      </div>
    `;

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jp-subscription-modal-open');
    };

    modal.addEventListener('click', (event) => {
      const clickedClose = event.target.closest('[data-jp-subscription-modal-close]');
      const clickedDialog = event.target.closest('.jp-subscription-modal__dialog');
      if (!clickedClose && clickedDialog) return;
      close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    document.body.append(modal);
    return modal;
  }

  function openSubscriptionInfoModal() {
    const modal = getSubscriptionInfoModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('jp-subscription-modal-open');
    modal.querySelector('.jp-subscription-modal__close')?.focus();
  }

  function sentenceCaseFrequency(text) {
    const cleaned = cleanText(text)
      .replace(/^subscribe\s*(?:&|and)\s*save\s*\d+%?\s*/i, '')
      .replace(/^delivery\s*/i, '')
      .replace(/^delivered\s*/i, '')
      .replace(/^receive\s*/i, '')
      .replace(/(once\s+a\s+month|every\s+\d+\s+months?|every\s+\d+\s+weeks?).*$/i, '$1')
      .replace(/\.\s*pay.*$/i, '')
      .replace(/save\s*R\s?[\d\s,.]+/gi, '')
      .replace(/subscribe\s*(?:&|and)\s*save\s*\d+%?/gi, '')
      .replace(moneyPattern, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return '';

    if (/once\s+a\s+month/i.test(cleaned)) {
      return 'Delivered once a month';
    }

    const everyMonths = cleaned.match(/every\s+(\d+)\s+months?/i);
    if (everyMonths) {
      return `Delivered once every ${everyMonths[1]} months`;
    }

    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  function getVisibleText(element) {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('script, style, svg').forEach((node) => node.remove());
    return cleanText(clone.textContent);
  }

  function getPrices(text) {
    return [...text.matchAll(moneyPattern)].map((match) => cleanText(match[0]));
  }

  function getSellingPlanGroups() {
    if (sellingPlanGroups) return sellingPlanGroups;

    const data = document.querySelector('script[data-jp-selling-plans]');
    if (!data) {
      sellingPlanGroups = [];
      return sellingPlanGroups;
    }

    try {
      sellingPlanGroups = JSON.parse(data.textContent || '[]');
    } catch (error) {
      sellingPlanGroups = [];
    }

    return sellingPlanGroups;
  }

  function getSelectedVariantFromPage() {
    const selectedVariant = document.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (!selectedVariant) return null;

    try {
      return JSON.parse(selectedVariant);
    } catch (error) {
      return null;
    }
  }

  function getCurrentVariant() {
    return currentVariant || getSelectedVariantFromPage();
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return `R ${amount.toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  function findSellingPlan(planId) {
    if (!planId) return null;

    for (const group of getSellingPlanGroups()) {
      for (const plan of group.selling_plans || []) {
        if (String(plan.id) === String(planId)) return plan;
      }
    }

    return null;
  }

  function getAdjustedPrice(cents, planId) {
    const plan = findSellingPlan(planId);
    const adjustment = plan?.price_adjustments?.[0];

    if (!adjustment) return Math.round(cents * 0.85);

    if (adjustment.value_type === 'percentage') {
      return Math.round(cents * (100 - Number(adjustment.value || 0)) / 100);
    }

    if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, cents - Number(adjustment.value || 0));
    }

    if (adjustment.value_type === 'price') {
      return Number(adjustment.value || cents);
    }

    return cents;
  }

  function getProductForm(preferredForm) {
    if (preferredForm?.matches?.('form[data-type="add-to-cart-form"]')) {
      bindSellingPlanForm(preferredForm);
      return preferredForm;
    }

    const form =
      document.querySelector('product-form form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('product-form form[action*="/cart/add"]');

    if (form) bindSellingPlanForm(form);
    return form;
  }

  function getSellingPlanInput(preferredForm) {
    const form = getProductForm(preferredForm);
    if (!form) return null;

    let input = form.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.append(input);
    }

    return input;
  }

  function removeSellingPlanInput(preferredForm) {
    getProductForm(preferredForm)?.querySelector('input[name="selling_plan"]')?.remove();
  }

  function textToFrequencyValue(text) {
    const cleaned = cleanText(text).toLowerCase();
    const monthMatch = cleaned.match(/(?:every|once\s+(?:a|every)?|delivered\s+once\s+every?)\s*(\d+)?\s*months?/i);

    if (/once\s+a\s+month|every\s+1\s+month|1\s+month/.test(cleaned)) return '1 Month';
    if (monthMatch) return `${monthMatch[1] || 1} Month${Number(monthMatch[1] || 1) === 1 ? '' : 's'}`;

    const weekMatch = cleaned.match(/(?:every|once\s+every|delivered\s+once\s+every)\s*(\d+)?\s*weeks?/i);
    if (weekMatch) return `${weekMatch[1] || 1} Week${Number(weekMatch[1] || 1) === 1 ? '' : 's'}`;

    return '';
  }

  function findSellingPlanId(option) {
    if (!option || option.isOneTime) return '';

    const sourceElements = [option.input, option.root].filter(Boolean);
    for (const element of sourceElements) {
      const dataset = element.dataset || {};
      const candidate =
        dataset.sellingPlan ||
        dataset.sellingPlanId ||
        dataset.sellingPlanid ||
        element.getAttribute('data-selling-plan') ||
        element.getAttribute('data-selling-plan-id');

      if (/^\d+$/.test(candidate || '')) return candidate;

      if (element.name === 'selling_plan' && /^\d+$/.test(element.value || '')) return element.value;
    }

    const frequency = textToFrequencyValue(`${option.title} ${option.text}`);
    if (!frequency) return '';

    for (const group of getSellingPlanGroups()) {
      for (const plan of group.selling_plans || []) {
        const values = (plan.options || []).map((item) => cleanText(item.value));
        if (values.includes(frequency)) return String(plan.id);
      }
    }

    return '';
  }

  function getActiveSellingPlanId() {
    return findSellingPlanId(activeSubscriptionOption);
  }

  window.JPSubscriptionPlanId = getActiveSellingPlanId;

  function syncSellingPlan(option, preferredForm) {
    activeSubscriptionOption = option && !option.isOneTime ? option : null;

    if (!activeSubscriptionOption) {
      removeSellingPlanInput(preferredForm);
      return;
    }

    const planId = getActiveSellingPlanId();
    if (!planId) {
      removeSellingPlanInput(preferredForm);
      return;
    }

    const input = getSellingPlanInput(preferredForm);
    if (input) input.value = planId;
  }

  function syncFormData(event) {
    const planId = getActiveSellingPlanId();
    if (planId) {
      event.formData.set('selling_plan', planId);
    } else {
      event.formData.delete('selling_plan');
    }
  }

  function bindSellingPlanForm(form) {
    if (!form || syncedForms.has(form)) return;
    syncedForms.add(form);
    form.addEventListener('formdata', syncFormData);
  }

  function optionRoot(input, widget) {
    const selectors = [
      'label',
      '[role="radio"]',
      '[class*="option"]',
      '[class*="Option"]',
      '[class*="card"]',
      '[class*="Card"]',
      '[class*="item"]',
      '[class*="Item"]',
    ];

    for (const selector of selectors) {
      const root = input.closest(selector);
      if (root && root !== widget && widget.contains(root)) return root;
    }

    return input.parentElement || input;
  }

  function uniqueOptions(options) {
    const seen = new Set();
    return options.filter((option) => {
      const key = `${option.input?.name || ''}-${option.input?.value || ''}-${option.title}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function findOptionInputs(widget) {
    const radios = [...widget.querySelectorAll('input[type="radio"]')].filter((input) => {
      const text = getVisibleText(optionRoot(input, widget));
      return /one\s*time|subscribe|month|week|deliver|every|once/i.test(text);
    });

    if (radios.length) return radios;

    return [...widget.querySelectorAll('button, [role="radio"], [tabindex]')].filter((element) => {
      const text = getVisibleText(element);
      return /one\s*time|subscribe|month|week|deliver|every|once/i.test(text);
    });
  }

  function parseOptions(widget) {
    const inputs = findOptionInputs(widget);
    const parsed = inputs.map((input) => {
      const root = optionRoot(input, widget);
      const text = getVisibleText(root);
      const prices = getPrices(text);
      const isOneTime = /one\s*time/i.test(text);
      const discount = text.match(/save\s*(?:R\s?[\d\s,.]+|\d+%)/i)?.[0] || 'Subscribe & Save 15%';

      let title = isOneTime ? 'One time purchase' : sentenceCaseFrequency(text);
      if (!isOneTime && (!title || /subscribe/i.test(title))) {
        title = text.match(/(?:once|every|delivered)\s+[^.]+/i)?.[0] || 'Subscription';
      }

      return {
        input,
        root,
        text,
        title,
        isOneTime,
        price: prices[0] || '',
        comparePrice: prices[1] || '',
        discount,
        checked: input.checked || input.getAttribute('aria-checked') === 'true',
      };
    });

    return uniqueOptions(parsed);
  }

  function clickOriginal(option) {
    if (!option) return;
    option.input.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    option.input.click?.();
    option.input.checked = true;
    option.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function createPrice(option, fallback, compareOverride) {
    const price = document.createElement('div');
    price.className = 'jp-subscription-price';
    price.textContent = option.price || fallback || '';

    const comparePrice = compareOverride || option.comparePrice;

    if (comparePrice && comparePrice !== option.price) {
      const compare = document.createElement('s');
      compare.textContent = comparePrice;
      price.append(' ', compare);
    }

    return price;
  }

  function renderPrice(priceElement, priceText, compareText) {
    if (!priceElement) return;

    priceElement.textContent = priceText || '';

    if (compareText && compareText !== priceText) {
      const compare = document.createElement('s');
      compare.textContent = compareText;
      priceElement.append(' ', compare);
    }
  }

  function updateSwitcherPrices(switcher) {
    const data = switcher?.__jpSubscriptionData;
    const variant = getCurrentVariant();
    if (!data || !variant?.price) return;

    const oneTimeText = formatMoney(variant.price);
    renderPrice(data.oneTimePrice, oneTimeText);

    const selectedSubscription = data.subscriptions[Number(data.select.value)] || data.subscriptions[0];
    const planId = findSellingPlanId(selectedSubscription);
    const subscriptionPrice = getAdjustedPrice(variant.price, planId);
    renderPrice(data.subscribePrice, formatMoney(subscriptionPrice), oneTimeText);
  }

  function updateAllSwitcherPrices() {
    document.querySelectorAll('.jp-subscription-switcher').forEach(updateSwitcherPrices);
  }

  function buildSwitcher(widget, options) {
    const oneTime = options.find((option) => option.isOneTime) || options[0];
    const subscriptions = options.filter((option) => !option.isOneTime);

    if (!oneTime || !subscriptions.length) return null;

    const selectedSubscription = subscriptions.find((option) => option.checked) || subscriptions[0];
    const isSubscriptionSelected = subscriptions.some((option) => option.checked);

    const switcher = document.createElement('div');
    switcher.className = 'jp-subscription-switcher';

    const oneTimeCard = document.createElement('button');
    oneTimeCard.type = 'button';
    oneTimeCard.className = `jp-subscription-card${isSubscriptionSelected ? '' : ' is-selected'}`;
    oneTimeCard.innerHTML = `
      <div class="jp-subscription-card__top">
        <span class="jp-subscription-radio" aria-hidden="true"></span>
        <p class="jp-subscription-title">One time purchase</p>
      </div>
    `;
    const oneTimePrice = createPrice(oneTime);
    oneTimeCard.querySelector('.jp-subscription-card__top').append(oneTimePrice);
    oneTimeCard.addEventListener('click', () => {
      clickOriginal(oneTime);
      syncSellingPlan(null);
      oneTimeCard.classList.add('is-selected');
      subscribeCard.classList.remove('is-selected');
    });

    const subscribeCard = document.createElement('div');
    subscribeCard.className = `jp-subscription-card${isSubscriptionSelected ? ' is-selected' : ''}`;
    subscribeCard.setAttribute('role', 'button');
    subscribeCard.setAttribute('tabindex', '0');
    subscribeCard.innerHTML = `
      <div class="jp-subscription-card__top">
        <span class="jp-subscription-radio" aria-hidden="true"></span>
        <p class="jp-subscription-title">Subscribe & Save 15%</p>
      </div>
      <div class="jp-subscription-details">
        <div class="jp-subscription-select-wrap">
          <select class="jp-subscription-select" aria-label="Choose subscription frequency"></select>
          <span class="jp-subscription-select-icon" aria-hidden="true"></span>
        </div>
        <ul class="jp-subscription-benefits">
          <li>Cancel anytime</li>
          <li>Money back guarantee</li>
          <li>Always 15% off</li>
        </ul>
        <button type="button" class="jp-subscription-info-link">How do subscriptions work?</button>
      </div>
    `;
    subscribeCard
      .querySelector('.jp-subscription-card__top')
      .append((() => {
        const price = createPrice(selectedSubscription, oneTime.price, oneTime.price);
        subscribeCard.__jpSubscriptionPrice = price;
        return price;
      })());

    const select = subscribeCard.querySelector('select');
    subscriptions.forEach((option, index) => {
      const item = document.createElement('option');
      item.value = String(index);
      item.textContent = option.title || `Subscription option ${index + 1}`;
      item.selected = option === selectedSubscription;
      select.append(item);
    });

    const selectSubscription = () => {
      const selected = subscriptions[Number(select.value)] || subscriptions[0];
      clickOriginal(selected);
      syncSellingPlan(selected);
      subscribeCard.classList.add('is-selected');
      oneTimeCard.classList.remove('is-selected');
      updateSwitcherPrices(switcher);
    };

    select.addEventListener('change', selectSubscription);
    subscribeCard.querySelector('.jp-subscription-info-link')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openSubscriptionInfoModal();
    });
    subscribeCard.addEventListener('click', (event) => {
      if (event.target === select) return;
      if (event.target.closest('.jp-subscription-info-link')) return;
      selectSubscription();
    });
    subscribeCard.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      selectSubscription();
    });

    switcher.append(oneTimeCard, subscribeCard);
    switcher.__jpSubscriptionData = {
      oneTimePrice,
      subscribePrice: subscribeCard.__jpSubscriptionPrice,
      subscriptions,
      select,
    };
    updateSwitcherPrices(switcher);
    syncSellingPlan(isSubscriptionSelected ? selectedSubscription : null);
    return switcher;
  }

  function findWidgets() {
    return [...document.querySelectorAll('.jp-product-app-block')].filter((block) => {
      if (block.hasAttribute(ENHANCED_ATTR)) return false;
      const text = getVisibleText(block);
      return /purchase\s+options|one\s*time|subscribe|once\s+a\s+month|every\s+\d+\s+months?/i.test(text);
    });
  }

  function enhanceWidget(widget) {
    const options = parseOptions(widget);
    const switcher = buildSwitcher(widget, options);

    if (!switcher) return;

    widget.before(switcher);
    widget.classList.add(ORIGINAL_CLASS);
    widget.setAttribute(ENHANCED_ATTR, 'true');
  }

  function enhanceAll() {
    findWidgets().forEach(enhanceWidget);
  }

  function scheduleEnhance() {
    window.clearTimeout(scheduleEnhance.timer);
    scheduleEnhance.timer = window.setTimeout(enhanceAll, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll, { once: true });
  } else {
    enhanceAll();
  }

  new MutationObserver(scheduleEnhance).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
      syncSellingPlan(activeSubscriptionOption, event.target);
    },
    true
  );

  document.addEventListener(
    'formdata',
    (event) => {
      if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;

      const planId = getActiveSellingPlanId();
      if (planId) {
        event.formData.set('selling_plan', planId);
      } else {
        event.formData.delete('selling_plan');
      }
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      currentVariant = event?.data?.variant || getSelectedVariantFromPage();
      updateAllSwitcherPrices();
    });
  }
})();
